import { isUrl } from '@flowus/common/embed-website';
import { BlockType, PermissionRole } from '@next-space/fe-api-idl';
import { contentToString } from '@next-space/fe-inlined';
import rehypeParse from 'rehype-parse';
import remarkParse from 'remark-parse';
import { textToSegments } from 'src/editor/utils/editor';
import { convertContentToSegments } from 'src/editor/utils/segments';
import { getPermissions } from 'src/hooks/share/use-permissions';
import { cache } from 'src/redux/store';
import { $appUiStateCache } from 'src/services/app';
import { isCollection } from 'src/utils/block-type-utils';
import { unified } from 'unified';
import { visit } from 'unist-util-visit';
import { check, isTableRow } from '../helper';
import type { NextSpaceInfo, NextSpaceText, PasteHook } from '../types';
import { TEXT_BLOCKS, TEXT_CELLS, TEXT_CONTENT, TEXT_HTML, TEXT_PLAIN } from '../types';
import { common } from './common';
import { parseHtmlToRecordCells } from './helper/html2cells';
import { pasteAnnotation } from './paste-annotation';
import { pasteBlock } from './paste-block';
import { pasteCell } from './paste-cell';
import { pasteContent } from './paste-content';
import { pasteFile } from './paste-file';
import { pasteHTML } from './paste-html';
import { pasteLink } from './paste-link';
import { pastePlain } from './paste-plain';
import { pasteTableCells } from './paste-table-cells';
import { getCurStore, setCutStore } from '../on-cut-copy/cut-store';
import { getCurrentSpaceId } from 'src/hooks/space/get-space';
import { moveBlock } from 'src/redux/managers/block/move';
import { transaction } from 'src/hooks/use-transaction';
import { updateBlock } from 'src/redux/managers/block/update';
import { handleSpace } from 'src/utils/clipboard';

export const onPaste = (props: PasteHook) => (event: ClipboardEvent) => {
  if (check()) return;
  if (props.readonly) return;
  // 文件夹页面不允许处理粘贴相关操作，这里如果要支持富文本粘贴的话逻辑太复杂，只能走系统处理
  const { pos, pageId, isInInlineEditable } = common();
  if (cache.blocks[pageId]?.type === BlockType.FOLDER) return;
  event.preventDefault();
  // 防止与富文本冲突
  event.stopImmediatePropagation();

  const { role } = getPermissions(pageId);

  if (role === PermissionRole.READER || role === PermissionRole.NONE) return;
  const locked = cache.blocks[pageId]?.data.format?.locked;
  if (locked) return;

  const cutStore = getCurStore();
  if (cutStore && getCurrentSpaceId() === cutStore.spaceId) {
    // 清空，第二次就会走原粘贴逻辑
    setCutStore(undefined);
    const { pos, where } = common();
    transaction(() => {
      if (pos === pageId) {
        where.first = true;
      } else if (pos && !where.last) {
        where.after = pos;
      }
      moveBlock(cutStore.blockIds, where);
      cutStore.blockIds.forEach((bid) => {
        updateBlock(bid, {
          status: 1, // 复活这些id
        });
      });
    });
    return;
  }

  props.closeModal?.($appUiStateCache.$blockMenuListId);

  let html = event.clipboardData?.getData(TEXT_HTML);
  let plain = event.clipboardData?.getData(TEXT_PLAIN);
  // 粘贴过来的替换掉&nbsp;的空格,否则英语断句会有问题。（W做了这个处理，N没做）
  plain = handleSpace(plain);
  html = handleSpace(html);
  const { content }: NextSpaceText = JSON.parse(event.clipboardData?.getData(TEXT_CONTENT) || '{}');
  const { records }: NextSpaceInfo = JSON.parse(event.clipboardData?.getData(TEXT_CELLS) || '{}');
  const info: NextSpaceInfo = JSON.parse(event.clipboardData?.getData(TEXT_BLOCKS) || '{}');

  if (plain?.includes('annotationId')) {
    try {
      const { annotationId, type }: { annotationId?: string; type?: string } = JSON.parse(
        plain || '{}'
      );
      if (annotationId) {
        void pasteAnnotation(props, annotationId, type);
        return;
      }
      // eslint-disable-next-line no-empty
    } catch (err) {}
  }
  if (event.clipboardData?.files.length) {
    const { files } = event.clipboardData;

    if (!html) {
      if (isCollection(cache.blocks[pageId]?.type)) {
        // 如果是在多维表下就不做操作了，否则会新建一个record并且这个record会显示无法访问
        return;
      }
      pasteFile(props, files);
      return;
    }

    // 有的软件（例如微软的）客户端 里复制文本会在剪切板里存一个图片 和 html
    // 从网页是右键保存也会有 html，所以需要解析 html 判断是否是图片
    const hast = unified().use(rehypeParse).parse(html);
    let bodyNode: any;
    visit(hast, (node) => {
      if (node.type === 'element' && node.tagName === 'body') {
        bodyNode = node;
      }
    });
    if (bodyNode) {
      const elements = bodyNode.children.filter((o: any) => o.type === 'element');
      if (elements.length === 1 && elements[0].tagName === 'img') {
        pasteFile(props, files);
        return;
      }
    }
  }

  const { blocks, userId, fromType, spaceId, hideSyncMenu, pageId: sourcePageId } = info;

  // 处理简单表格的粘贴（选中单元格内文字评论，isInInlineEditable为true,不走里面的逻辑）
  if (isTableRow() && !isInInlineEditable) {
    if (content) {
      const segments = convertContentToSegments(content);
      pasteTableCells(props, [{ cells: [segments] }]);
      return;
    }

    if (records) {
      pasteTableCells(props, records);
      return;
    }

    if (html) {
      const records = parseHtmlToRecordCells(html);
      if (records) {
        pasteTableCells(props, records);
        return;
      }
    }

    // 这里按着 notion 来的，块复制贴 plain text
    if (blocks || plain) {
      if (!plain) return;

      if (_isUrl(plain)) {
        pasteLink(props, event, plain);
        return;
      }

      // [ 打头的 plain 可能会是 markdown 格式的链接
      if (/^\[/.test(plain)) {
        const hast = unified().use(remarkParse).parse(plain);
        if (hast.children.length === 1 && hast.children[0]?.type === 'paragraph') {
          const { children } = hast.children[0];
          if (children.length === 1 && children[0] && children[0].type === 'link') {
            const { url, children: _children } = children[0];
            const text = _children[0]?.type === 'text' ? _children[0]?.value : '';
            pasteLink(props, event, text, url);
            return;
          }
        }
      }

      const segments = textToSegments(plain);
      pasteTableCells(props, [{ cells: [segments] }]);
      return;
    }

    return;
  }

  if (!isInInlineEditable && blocks && userId && spaceId) {
    pasteBlock(props, blocks, userId, spaceId, fromType, sourcePageId, hideSyncMenu);
    return;
  }

  // 处理多维表里的粘贴
  if (!isInInlineEditable && cache.ui.selectedCells.length) {
    if (records) {
      pasteCell(props, records);
      return;
    }

    if (content) {
      const segments = convertContentToSegments(content);
      pasteCell(props, [{ cells: [segments] }]);
      return;
    }

    if (html) {
      const records = parseHtmlToRecordCells(html);
      if (records) {
        pasteCell(props, records);
        return;
      }
    }

    plain && pasteCell(props, plain);
    return;
  }

  if (content) {
    const text = contentToString(content);
    if (_isUrl(text)) {
      pasteLink(props, event, text);
      return;
    }

    pasteContent(props, content);
    return;
  }

  if (plain && _isUrl(plain)) {
    pasteLink(props, event, plain);
    return;
  }

  if (html && pos !== sourcePageId) {
    if (/data-inlined-content/.test(html) && plain) {
      pastePlain(props, plain);
      return;
    }
    pasteHTML(props, html);
    return;
  }

  const _isCollection = isCollection(cache.blocks[pageId]?.type);
  if (!pos && _isCollection) {
    // 如果是在多维表下就不做操作了，否则会新建一个record并且这个record会显示无法访问
    return;
  }

  if (plain) {
    pastePlain(props, plain);
  }
};

const _isUrl = (text: string) => {
  return isUrl(text);
};
